(() => {
  'use strict';

  const SELECTORS = {
    hero: '[data-mc-hero]',
    carousel: '[data-mc-carousel]',
    wishlistButton: '[data-mc-wishlist-toggle]',
    wishlistDrawer: '[data-mc-wishlist-drawer]',
    wishlistItem: '[data-mc-wishlist-item]',
    backToTop: '[data-mc-back-to-top]'
  };

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function escapeHTML(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function initHeroes(scope = document) {
    scope.querySelectorAll(SELECTORS.hero).forEach((hero) => {
      if (hero.dataset.mcInitialized === 'true') return;
      hero.dataset.mcInitialized = 'true';

      const slides = Array.from(hero.querySelectorAll('[data-mc-hero-slide]'));
      const dots = Array.from(hero.querySelectorAll('[data-mc-hero-dot]'));
      const delay = Math.max(3000, Number(hero.dataset.autoplaySpeed || 6) * 1000);
      const autoplay = hero.dataset.autoplay === 'true' && slides.length > 1 && !prefersReducedMotion;
      let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      let timer;

      const show = (nextIndex) => {
        activeIndex = (nextIndex + slides.length) % slides.length;
        slides.forEach((slide, index) => {
          const active = index === activeIndex;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
        });
        dots.forEach((dot, index) => {
          const active = index === activeIndex;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-current', active ? 'true' : 'false');
        });
      };

      const stop = () => window.clearInterval(timer);
      const start = () => {
        stop();
        if (autoplay) timer = window.setInterval(() => show(activeIndex + 1), delay);
      };

      dots.forEach((dot, index) => dot.addEventListener('click', () => {
        show(index);
        start();
      }));

      hero.addEventListener('mouseenter', stop);
      hero.addEventListener('mouseleave', start);
      hero.addEventListener('focusin', stop);
      hero.addEventListener('focusout', start);

      hero.addEventListener('touchstart', (event) => {
        hero.dataset.touchX = String(event.touches[0].clientX);
        stop();
      }, { passive: true });
      hero.addEventListener('touchend', (event) => {
        const startX = Number(hero.dataset.touchX || 0);
        const endX = event.changedTouches[0].clientX;
        if (Math.abs(startX - endX) > 50) show(activeIndex + (startX > endX ? 1 : -1));
        start();
      }, { passive: true });

      show(activeIndex);
      start();
    });
  }

  function initCarousels(scope = document) {
    scope.querySelectorAll(SELECTORS.carousel).forEach((carousel) => {
      if (carousel.dataset.mcInitialized === 'true') return;
      carousel.dataset.mcInitialized = 'true';

      const track = carousel.querySelector('[data-mc-carousel-track]');
      const previous = carousel.querySelector('[data-mc-carousel-prev]');
      const next = carousel.querySelector('[data-mc-carousel-next]');
      const dots = carousel.querySelector('[data-mc-carousel-dots]');
      if (!track) return;

      let positions = [0];

      const currentPage = () => {
        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;
        positions.forEach((position, index) => {
          const distance = Math.abs(track.scrollLeft - position);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });
        return closestIndex;
      };

      const renderDots = () => {
        if (!dots) return;
        dots.innerHTML = '';
        for (let index = 0; index < positions.length; index += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = `mc-carousel__page${index === currentPage() ? ' is-active' : ''}`;
          dot.setAttribute('aria-label', `${index + 1}. sayfayı göster`);
          dot.addEventListener('click', () => track.scrollTo({ left: positions[index], behavior: prefersReducedMotion ? 'auto' : 'smooth' }));
          dots.appendChild(dot);
        }
      };

      const update = () => {
        const items = Array.from(track.children);
        const firstItem = items[0];
        const styles = window.getComputedStyle(track);
        const gap = Number.parseFloat(styles.columnGap || styles.gap || '0');
        const itemWidth = firstItem?.getBoundingClientRect().width || track.clientWidth;
        const visible = Math.max(1, Math.round((track.clientWidth + gap) / Math.max(itemWidth + gap, 1)));
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        positions = [];
        for (let index = 0; index < items.length; index += visible) {
          const position = Math.min(index * (itemWidth + gap), maxScroll);
          if (!positions.some((saved) => Math.abs(saved - position) < 2)) positions.push(position);
        }
        if (!positions.length) positions = [0];
        renderDots();
      };

      const updateActiveDot = () => {
        if (!dots) return;
        Array.from(dots.children).forEach((dot, index) => dot.classList.toggle('is-active', index === currentPage()));
      };

      previous?.addEventListener('click', () => {
        const index = Math.max(0, currentPage() - 1);
        track.scrollTo({ left: positions[index], behavior: prefersReducedMotion ? 'auto' : 'smooth' });
      });
      next?.addEventListener('click', () => {
        const index = Math.min(positions.length - 1, currentPage() + 1);
        track.scrollTo({ left: positions[index], behavior: prefersReducedMotion ? 'auto' : 'smooth' });
      });
      track.addEventListener('scroll', updateActiveDot, { passive: true });

      const observer = new ResizeObserver(update);
      observer.observe(track);
      update();
    });
  }

  const wishlist = {
    key: 'mycorner-baby-wishlist-v1',
    items: [],

    load() {
      try {
        const value = JSON.parse(window.localStorage.getItem(this.key) || '[]');
        this.items = Array.isArray(value) ? value : [];
      } catch (_error) {
        this.items = [];
      }
    },

    save() {
      try {
        window.localStorage.setItem(this.key, JSON.stringify(this.items));
      } catch (_error) {
        // Browsers may block storage in privacy mode; the UI still works for this page view.
      }
    },

    has(handle) {
      return this.items.some((item) => item.handle === handle);
    },

    toggle(item) {
      const index = this.items.findIndex((saved) => saved.handle === item.handle);
      if (index >= 0) this.items.splice(index, 1);
      else this.items.unshift(item);
      this.save();
      this.render();
    },

    remove(handle) {
      this.items = this.items.filter((item) => item.handle !== handle);
      this.save();
      this.render();
    },

    render() {
      document.querySelectorAll(SELECTORS.wishlistItem).forEach((button) => {
        const active = this.has(button.dataset.productHandle);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
        const label = button.querySelector('[data-mc-wishlist-label]');
        if (label) label.textContent = active ? 'Favorilerden kaldır' : 'Favorilere ekle';
      });

      document.querySelectorAll('[data-mc-wishlist-count]').forEach((counter) => {
        counter.textContent = this.items.length ? String(this.items.length) : '';
      });

      document.querySelectorAll('[data-mc-wishlist-list]').forEach((list) => {
        if (!this.items.length) {
          list.innerHTML = '<div class="mc-wishlist-drawer__empty"><p>Henüz favori ürününüz bulunmuyor.</p><a class="mc-button" href="/collections/all">Ürünleri keşfet</a></div>';
          return;
        }

        list.innerHTML = this.items.map((item) => `
          <article class="mc-wishlist-drawer__item">
            <a href="${escapeHTML(item.url)}" tabindex="-1">
              ${item.image ? `<img src="${escapeHTML(item.image)}" alt="" width="180" height="210" loading="lazy">` : ''}
            </a>
            <div>
              <a class="mc-wishlist-drawer__item-title" href="${escapeHTML(item.url)}">${escapeHTML(item.title)}</a>
              <span>${escapeHTML(item.price)}</span>
            </div>
            <button type="button" class="mc-wishlist-drawer__remove" data-mc-wishlist-remove="${escapeHTML(item.handle)}" aria-label="Favorilerden kaldır">×</button>
          </article>
        `).join('');

        list.querySelectorAll('[data-mc-wishlist-remove]').forEach((button) => {
          button.addEventListener('click', () => this.remove(button.dataset.mcWishlistRemove));
        });
      });
    }
  };

  function initWishlist(scope = document) {
    scope.querySelectorAll(SELECTORS.wishlistItem).forEach((button) => {
      if (button.dataset.mcInitialized === 'true') return;
      button.dataset.mcInitialized = 'true';
      button.addEventListener('click', () => {
        wishlist.toggle({
          handle: button.dataset.productHandle,
          title: button.dataset.productTitle,
          url: button.dataset.productUrl,
          image: button.dataset.productImage,
          price: button.dataset.productPrice
        });
      });
    });

    scope.querySelectorAll(SELECTORS.wishlistButton).forEach((button) => {
      if (button.dataset.mcInitialized === 'true') return;
      button.dataset.mcInitialized = 'true';
      button.addEventListener('click', () => toggleWishlistDrawer(true));
    });

    const drawer = document.querySelector(SELECTORS.wishlistDrawer);
    if (drawer && drawer.dataset.mcInitialized !== 'true') {
      drawer.dataset.mcInitialized = 'true';
      drawer.querySelectorAll('[data-mc-wishlist-close]').forEach((button) => button.addEventListener('click', () => toggleWishlistDrawer(false)));
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && drawer.classList.contains('is-open')) toggleWishlistDrawer(false);
      });
    }

    wishlist.render();
  }

  function toggleWishlistDrawer(open) {
    const drawer = document.querySelector(SELECTORS.wishlistDrawer);
    if (!drawer) return;
    drawer.classList.toggle('is-open', open);
    drawer.setAttribute('aria-hidden', String(!open));
    document.documentElement.classList.toggle('overflow-hidden', open);
    if (open) drawer.querySelector('[data-mc-wishlist-close]')?.focus();
  }

  function initBackToTop(scope = document) {
    const button = scope.querySelector(SELECTORS.backToTop) || document.querySelector(SELECTORS.backToTop);
    if (!button || button.dataset.mcInitialized === 'true') return;
    button.dataset.mcInitialized = 'true';
    const update = () => button.classList.toggle('is-visible', window.scrollY > 500);
    window.addEventListener('scroll', update, { passive: true });
    button.addEventListener('click', () => window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' }));
    update();
  }

  function init(scope = document) {
    initHeroes(scope);
    initCarousels(scope);
    initWishlist(scope);
    initBackToTop(scope);
  }

  wishlist.load();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('[data-mc-hero-slide]');
    if (!slide) return;
    const hero = slide.closest(SELECTORS.hero);
    const slides = Array.from(hero.querySelectorAll('[data-mc-hero-slide]'));
    hero.querySelectorAll('[data-mc-hero-dot]')[slides.indexOf(slide)]?.click();
  });
})();
